<script setup>
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import providersAPI from 'dashboard/api/appointmentProviders';
import appointmentsAPI from 'dashboard/api/appointments';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineOptions({ name: 'AppointmentsSettings' });

const { t, locale } = useI18n();

const providers = ref([]);
const appointments = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const form = ref({});

// IANA names given by the browser; a short list for the browsers that cannot give them
const timeZones = Intl.supportedValuesOf?.('timeZone') || [
  'America/Bogota',
  'America/Lima',
  'America/Mexico_City',
  'America/Santiago',
  'America/Argentina/Buenos_Aires',
  'Europe/Madrid',
  'UTC',
];

const weekdays = computed(() =>
  [0, 1, 2, 3, 4, 5, 6].map(day => ({
    value: day,
    label: new Date(2024, 0, 7 + day).toLocaleDateString(locale.value, {
      weekday: 'long',
    }),
  }))
);

const fetchAll = async () => {
  isLoading.value = true;
  try {
    const [providersResponse, appointmentsResponse] = await Promise.all([
      providersAPI.get(),
      appointmentsAPI.get(),
    ]);
    providers.value = providersResponse.data.payload;
    appointments.value = appointmentsResponse.data.payload;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchAll);

const scheduleText = provider =>
  provider.availabilities
    .map(
      a =>
        `${weekdays.value[a.weekday].label} ${a.start_hour}-${a.end_hour}h (${a.capacity_per_hour})`
    )
    .join(' · ');

const openForm = provider => {
  form.value = provider
    ? JSON.parse(JSON.stringify(provider))
    : {
        name: '',
        location: '',
        notification_email: '',
        time_zone: 'America/Bogota',
        max_per_hour: 4,
        active: true,
        availabilities: [],
      };
  showForm.value = true;
};

const addRow = () => {
  form.value.availabilities.push({
    weekday: 1,
    start_hour: 8,
    end_hour: 17,
    capacity_per_hour: 1,
  });
};

const removeRow = index => {
  const [row] = form.value.availabilities.splice(index, 1);
  if (row.id) {
    form.value.removed = [...(form.value.removed || []), row.id];
  }
};

const save = async () => {
  const { id, removed = [], availabilities, ...attributes } = form.value;
  const payload = {
    ...attributes,
    availabilities_attributes: [
      ...availabilities,
      ...removed.map(rowId => ({ id: rowId, _destroy: true })),
    ],
  };
  try {
    if (id) {
      await providersAPI.update(id, { appointment_provider: payload });
    } else {
      await providersAPI.create({ appointment_provider: payload });
    }
    showForm.value = false;
    useAlert(t('APPOINTMENTS.SAVE_SUCCESS'));
    fetchAll();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('APPOINTMENTS.SAVE_ERROR'));
  }
};

const cancelAppointment = async appointment => {
  await appointmentsAPI.update(appointment.id, {
    appointment: { status: 'cancelled' },
  });
  fetchAll();
};
</script>

<template>
  <SettingsLayout :is-loading="isLoading" :no-records-found="false">
    <template #header>
      <BaseSettingsHeader
        :title="$t('APPOINTMENTS.HEADER')"
        :description="$t('APPOINTMENTS.DESCRIPTION')"
      >
        <template #actions>
          <a
            :href="appointmentsAPI.exportUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button :label="$t('APPOINTMENTS.EXPORT')" slate faded size="sm" />
          </a>
          <Button
            :label="$t('APPOINTMENTS.PROVIDERS.ADD')"
            size="sm"
            @click="openForm()"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <ul class="flex flex-col">
        <li
          v-for="item in providers"
          :key="item.id"
          class="flex flex-col gap-2 py-4 border-b border-n-weak md:flex-row md:items-start md:justify-between md:gap-6"
        >
          <div class="flex flex-col gap-1 min-w-0 md:w-1/3">
            <span class="text-heading-3 text-n-slate-12 truncate">
              {{ item.name }}
            </span>
            <span class="text-body-main text-n-slate-11">
              {{ $t('APPOINTMENTS.PROVIDERS.MAX') }}: {{ item.max_per_hour }}
            </span>
          </div>
          <span class="text-body-main text-n-slate-11 md:flex-1">
            {{ scheduleText(item) }}
          </span>
          <div class="flex justify-end">
            <Button icon="i-woot-edit-pen" slate sm @click="openForm(item)" />
          </div>
        </li>
      </ul>

      <h2 class="mt-8 mb-2 text-heading-2 text-n-slate-12">
        {{ $t('APPOINTMENTS.LIST.TITLE') }}
      </h2>
      <p
        v-if="!appointments.length"
        class="py-8 text-center text-body-main text-n-slate-11"
      >
        {{ $t('APPOINTMENTS.LIST.404') }}
      </p>
      <ul class="flex flex-col">
        <li
          v-for="item in appointments"
          :key="item.id"
          class="flex flex-col gap-2 py-4 border-b border-n-weak md:flex-row md:items-center md:justify-between md:gap-6"
        >
          <div class="flex flex-col gap-1 min-w-0 md:w-1/3">
            <span class="text-heading-3 text-n-slate-12">
              {{ new Date(item.starts_at).toLocaleString(locale) }}
            </span>
            <span class="text-body-main text-n-slate-11">
              {{ item.provider_name }}
            </span>
          </div>
          <span class="text-body-main text-n-slate-11 break-words md:flex-1">
            {{ item.contact_name }} {{ item.contact_phone }}
          </span>
          <div class="flex items-center justify-between gap-3 md:justify-end">
            <span class="text-body-main text-n-slate-11">{{
              item.status
            }}</span>
            <Button
              v-if="item.status === 'booked'"
              :label="$t('APPOINTMENTS.LIST.CANCEL')"
              slate
              faded
              sm
              @click="cancelAppointment(item)"
            />
          </div>
        </li>
      </ul>
    </template>

    <woot-modal v-model:show="showForm" :on-close="() => (showForm = false)">
      <div class="flex flex-col gap-4 p-4 md:p-8">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ $t('APPOINTMENTS.PROVIDERS.FORM_TITLE') }}
        </h3>
        <label class="flex flex-col gap-1 text-n-slate-12">
          {{ $t('APPOINTMENTS.PROVIDERS.NAME') }}
          <input v-model="form.name" type="text" />
        </label>
        <label class="flex flex-col gap-1 text-n-slate-12">
          {{ $t('APPOINTMENTS.PROVIDERS.LOCATION') }}
          <input v-model="form.location" type="text" />
        </label>
        <label class="flex flex-col gap-1 text-n-slate-12">
          {{ $t('APPOINTMENTS.PROVIDERS.EMAIL') }}
          <input v-model="form.notification_email" type="email" />
        </label>
        <label class="flex flex-col gap-1 text-n-slate-12">
          {{ $t('APPOINTMENTS.PROVIDERS.MAX') }}
          <input v-model.number="form.max_per_hour" type="number" min="1" />
          <span class="text-body-main text-n-slate-11">
            {{ $t('APPOINTMENTS.PROVIDERS.MAX_HELP') }}
          </span>
        </label>
        <label class="flex flex-col gap-1 text-n-slate-12">
          {{ $t('APPOINTMENTS.PROVIDERS.TIME_ZONE') }}
          <input
            v-model="form.time_zone"
            type="text"
            list="appointment-time-zones"
          />
          <datalist id="appointment-time-zones">
            <option v-for="zone in timeZones" :key="zone" :value="zone" />
          </datalist>
          <span class="text-body-main text-n-slate-11">
            {{ $t('APPOINTMENTS.PROVIDERS.TIME_ZONE_HELP') }}
          </span>
        </label>
        <div class="flex flex-col gap-2">
          <span class="text-n-slate-12">
            {{ $t('APPOINTMENTS.PROVIDERS.SCHEDULE') }}
          </span>
          <div
            v-for="(row, index) in form.availabilities"
            :key="index"
            class="grid grid-cols-3 gap-2 items-end p-3 rounded-lg bg-n-alpha-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] md:p-0 md:bg-transparent"
          >
            <select
              v-model.number="row.weekday"
              class="col-span-2 m-0 md:col-span-1"
            >
              <option
                v-for="day in weekdays"
                :key="day.value"
                :value="day.value"
              >
                {{ day.label }}
              </option>
            </select>
            <div class="flex justify-end md:order-last">
              <Button icon="i-woot-bin" slate sm @click="removeRow(index)" />
            </div>
            <label class="flex flex-col gap-1 text-body-main text-n-slate-11">
              {{ $t('APPOINTMENTS.PROVIDERS.FROM') }}
              <input
                v-model.number="row.start_hour"
                type="number"
                min="0"
                max="23"
                class="m-0"
              />
            </label>
            <label class="flex flex-col gap-1 text-body-main text-n-slate-11">
              {{ $t('APPOINTMENTS.PROVIDERS.TO') }}
              <input
                v-model.number="row.end_hour"
                type="number"
                min="1"
                max="24"
                class="m-0"
              />
            </label>
            <label class="flex flex-col gap-1 text-body-main text-n-slate-11">
              {{ $t('APPOINTMENTS.PROVIDERS.CAPACITY') }}
              <input
                v-model.number="row.capacity_per_hour"
                type="number"
                min="1"
                :max="form.max_per_hour"
                class="m-0"
              />
            </label>
          </div>
          <Button
            :label="$t('APPOINTMENTS.PROVIDERS.ADD_ROW')"
            slate
            faded
            sm
            @click="addRow"
          />
          <span class="text-body-main text-n-slate-11">
            {{ $t('APPOINTMENTS.PROVIDERS.ROW_HELP') }}
          </span>
        </div>
        <label class="flex items-center gap-2 text-n-slate-12">
          <input v-model="form.active" type="checkbox" class="m-0" />
          {{ $t('APPOINTMENTS.PROVIDERS.ACTIVE') }}
        </label>
        <div class="flex justify-end gap-2">
          <Button
            :label="$t('APPOINTMENTS.CANCEL')"
            slate
            faded
            @click="showForm = false"
          />
          <Button :label="$t('APPOINTMENTS.SAVE')" @click="save" />
        </div>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
